"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import { api } from "@/lib/api/client";

import { MODULES, SETTINGS, isActive } from "./nav-items";
import { ProjectSwitcher } from "./project-switcher";
import { SourceStatus } from "./source-status";

type Props = {
  id: string;
  /** Mobil: Drawer geöffnet. Ab `lg` ist die Navigation immer sichtbar. */
  open: boolean;
  onNavigate: () => void;
};

const itemBase = "flex min-h-11 items-center rounded-md px-3 text-sm";

export function Sidebar({ id, open, onNavigate }: Props) {
  const pathname = usePathname();
  const router = useRouter();

  const logout = async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } finally {
      router.replace("/login");
    }
  };

  return (
    <nav
      id={id}
      aria-label="Hauptnavigation"
      className={`${open ? "flex" : "hidden"} fixed inset-y-0 left-0 z-40 w-[280px] max-w-[85vw] flex-col gap-6 overflow-y-auto bg-nav px-4 py-6 text-nav-text lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[248px] lg:max-w-none lg:shrink-0`}
    >
      <div className="flex items-center gap-2.5 px-2">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#8fb3dd" strokeWidth="1.8" aria-hidden="true">
          <circle cx="14" cy="14" r="11" />
          <path d="M3 14h22M14 3c4 4 4 18 0 22M14 3c-4 4-4 18 0 22" />
        </svg>
        <span className="text-[17px] font-semibold tracking-tight text-white">GEO Suite</span>
        <button
          type="button"
          onClick={onNavigate}
          className="ml-auto h-11 rounded-md border border-nav-line px-3 text-[13px] lg:hidden"
        >
          Schließen
        </button>
      </div>

      <ProjectSwitcher />

      <div className="flex flex-col gap-0.5">
        <h2 className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-nav-muted">
          Module
        </h2>
        {MODULES.map((item) => {
          const active = isActive(item.href, pathname);
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              aria-current={active ? "page" : undefined}
              className={`${itemBase} gap-3 ${active ? "bg-nav-active font-medium text-white" : "hover:bg-nav-raised"}`}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d={item.icon} />
              </svg>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-auto flex flex-col gap-2">
        <SourceStatus />
        <div className="mt-1 flex flex-col">
          {SETTINGS.map((item) => {
            const active = isActive(item.href, pathname);
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={`${itemBase} text-[13px] text-nav-link ${active ? "bg-nav-active font-medium" : "hover:bg-nav-raised"}`}
              >
                {item.label}
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => void logout()}
            className={`${itemBase} text-left text-[13px] text-nav-muted hover:bg-nav-raised`}
          >
            Abmelden
          </button>
        </div>
      </div>
    </nav>
  );
}
